import client from "./axiosClient";

const getSuperAdminToken = () => {
  if (typeof window === "undefined") return null;
  return localStorage.getItem("superAdminToken");
};

const withSuperAdminAuth = (config = {}) => {
  const token = getSuperAdminToken();
  if (!token) return config;
  return {
    ...config,
    headers: {
      ...(config.headers || {}),
      Authorization: `Bearer ${token}`,
    },
  };
};

const http = {
  get: (url, config) => client.get(url, config).then((r) => r.data),
  post: (url, data, config) => client.post(url, data, config).then((r) => r.data),
  put: (url, data, config) => client.put(url, data, config).then((r) => r.data),
  patch: (url, data, config) => client.patch(url, data, config).then((r) => r.data),
  del: (url, config) => client.delete(url, config).then((r) => r.data),
};

export const Api = {
  auth: {
    login: (payload) => http.post("/auth/login", payload),
    mobileLogin: (payload) => http.post("/auth/mobile-login", payload),
    register: (payload) => http.post("/users", payload),
    me: () => http.get("/auth/profile"),
    logout: () => http.post("/auth/logout"),
  },
  users: {
    list: (params) => http.get("/users", { params }),
    get: (id) => http.get(`/users/${id}`),
    create: (payload) => http.post("/users", payload),
    update: (id, payload) => http.put(`/users/${id}`, payload),
    remove: (id) => http.del(`/users/${id}`),
    discover: (params) => http.get("/users/discover", { params }),
    filter: (params) => http.get("/users/filter", { params }),
    sendOtp: (payload) => http.post("/auth/send-otp", payload),
    resendOtp: (payload) => http.post("/auth/resend-otp", payload),
    verifyOtp: (payload) => http.post("/auth/verify-otp", payload),
    syncFromChatbot: (payload) => http.post("/users/chatbot/sync", payload),

    // Forgot Password and Reset Password endpoints
    forgotPassword: (payload) => http.post("/users/forgot-password", payload),
    resetPassword: (payload) => http.post("/users/reset-password", payload),
    verifyToken: (payload) => http.post("/users/verify-token", payload),
  },
  roles: {
    list: (params) => http.get("/roles", { params }),
    get: (id) => http.get(`/roles/${id}`),
    getActive: () => http.get("/roles/active"),
    create: (payload) => http.post("/roles", payload),
    update: (id, payload) => http.put(`/roles/${id}`, payload),
    remove: (id) => http.del(`/roles/${id}`),
    toggleStatus: (id) => http.patch(`/roles/${id}/toggle-status`),
  },
  requests: {
    createRequest: (payload) => {
      return http.post("/requests", payload);
    },
    updateRequest: (id, payload) => http.put(`/requests/${id}`, payload),
    getUserRequests: (userId, params) => http.get(`/requests/${userId}`, { params }),
    getSentRequests: (userId, params) => http.get(`/requests/${userId}/sent`, { params }),
    getGeneral: (userId, params) => http.get(`/requests/${userId}/general`, { params }),
    getMessageInquiries: (userId) => http.get(`/requests/${userId}/inquiries`),
    accept: (id) => http.patch(`/requests/${id}/accept`),
  },
  locations: {
    countries: {
      list: (params) => http.get("/location/countries", { params }),
      get: (id) => http.get(`/location/countries/${id}`),
      create: (payload) => http.post("/location/countries", payload),
      update: (id, payload) => http.put(`/location/countries/${id}`, payload),
      remove: (id) => http.del(`/location/countries/${id}`),
    },
    states: {
      list: (params) => http.get("/location/states", { params }),
      get: (id) => http.get(`/location/states/${id}`),
      create: (payload) => http.post("/location/states", payload),
      update: (id, payload) => http.put(`/location/states/${id}`, payload),
      remove: (id) => http.del(`/location/states/${id}`),
    },
    cities: {
      list: (params) => http.get("/location/cities", { params }),
      listAll: (params) => http.get("/location/all-cities", { params }),
      get: (id) => http.get(`/location/cities/${id}`),
      create: (payload) => http.post("/location/cities", payload),
      update: (id, payload) => http.put(`/location/cities/${id}`, payload),
      remove: (id) => http.del(`/location/cities/${id}`),
    },
  },
  categories: {
    list: (params) => http.get("/categories", { params }),
    get: (id) => http.get(`/categories/${id}`),
    create: (payload) => http.post("/categories", payload),
    update: (id, payload) => http.put(`/categories/${id}`, payload),
    remove: (id) => http.del(`/categories/${id}`),
    getActive: () => http.get("/categories/active"),
    toggleStatus: (id) => http.patch(`/categories/${id}/toggle-status`),
  },
  industries: {
    list: (params) => http.get("/industries", { params }),
    get: (id) => http.get(`/industries/${id}`),
    create: (payload) => http.post("/industries", payload),
    update: (id, payload) => http.put(`/industries/${id}`, payload),
    remove: (id) => http.del(`/industries/${id}`),
    getActive: () => http.get("/industries/active"),
    toggleStatus: (id) => http.patch(`/industries/${id}/toggle-status`),
  },
  compatibility: {
    calculate: (companyId) => http.get(`/compatibility/${companyId}/compatibility`),
    getScores: (companyId) => http.get(`/compatibility/${companyId}/scores`),
  },
  companies: {
    list: (params) => http.get("/companies", { params }),
    fetchAll: (params) => http.get("/companies/all", { params }),
    get: (id) => http.get(`/companies/${id}`),
    getBySlug: (slug) => http.get(`/company/${encodeURIComponent(slug)}`),
    getDMSCompany: () => http.get("/companies/dms/company"),
    listTallyDiscovered: (clientId) =>
      http.get("/companies/tally-discovered", { params: { client_id: clientId } }),
    updateTallySyncPreferences: (payload) =>
      http.patch("/companies/tally-sync-preferences", payload),
    listFileBakFiles: (clientId) =>
      http.get("/companies/file-bak-files", { params: { client_id: clientId } }),
    selectFileBak: (payload) => http.post("/companies/file-bak-select", payload),
    listForManuDistriMapping: (params) => http.get("/companies/manu-distri-mapping", { params }),
    getChildCompaniesByParentId: (parentCompanyId) =>
      http.get(`/companies/mapping/${parentCompanyId}`),
    create: (payload) => {
      const config =
        payload instanceof FormData
          ? {
              headers: { "Content-Type": "multipart/form-data" },
            }
          : undefined;
      return http.post("/companies", payload, config);
    },
    update: (id, payload) => {
      const config =
        payload instanceof FormData
          ? {
              headers: { "Content-Type": "multipart/form-data" },
            }
          : undefined;
      return http.put(`/companies/${id}`, payload, config);
    },
    remove: (id) => http.del(`/companies/${id}`),
    filter: (params) => http.get("/companies/filter", { params }),
    profileSetup: (payload) => {
      const config =
        payload instanceof FormData
          ? {
              headers: { "Content-Type": "multipart/form-data" },
            }
          : undefined;
      return http.post("/companies/profile/setup", payload, config);
    },
    listDocuments: (companyId) => http.get(`/companies/${companyId}/documents`),
    createDocument: (payload) => http.post("/companies/documents", payload),
    saveForLater: (companyId) => http.post(`/companies/${companyId}/save-for-later`),
    removeFromSaved: (companyId) => http.del(`/companies/${companyId}/save-for-later`),
    getSavedForLater: (userId) => http.get(`/companies/saved-for-later/${userId}`),

    listActiveInactive: (params) => {
      return http.get("/companies", {
        params: {
          ...params,
          _activeInactive: true,
        },
        ...withSuperAdminAuth(),
      });
    },
    toggleStatus: (id) => http.patch(`/companies/${id}/toggle-status`, {}, withSuperAdminAuth()),
  },
  products: {
    list: (params) => http.get("/products", { params }),
    get: (id) => http.get(`/products/${id}`),
    getBySlug: (identifier) => {
      if (!identifier) {
        throw new Error("Product slug is required");
      }
      if (typeof identifier === "object") {
        const { companySlug, productSlug } = identifier;
        if (!companySlug || !productSlug) {
          throw new Error("companySlug and productSlug are required");
        }
        return http.get(
          `/products/slug/${encodeURIComponent(companySlug)}/${encodeURIComponent(productSlug)}`
        );
      }
      return http.get(`/products/slug/${encodeURIComponent(identifier)}`);
    },
    getRelatedByCompany: (companyId, params) =>
      http.get(`/products/company/${companyId}/related`, { params }),
    listByCompany: (companyId, params) => http.get(`/products/company/${companyId}`, { params }),
    getBestSelling: (params) => http.get("/products/bestselling", { params }),
    getBatch: (companyIds, limit = 10) =>
      http.get("/products/batch", { params: { company_ids: companyIds.join(","), limit } }),
    create: (payload) => {
      const config =
        payload instanceof FormData
          ? {
              headers: { "Content-Type": "multipart/form-data" },
            }
          : undefined;
      return http.post("/products/create", payload, config);
    },
    update: (id, payload) => {
      const config =
        payload instanceof FormData
          ? {
              headers: { "Content-Type": "multipart/form-data" },
            }
          : undefined;
      return http.put(`/products/${id}`, payload, config);
    },
    remove: (id) => http.del(`/products/${id}`),
    addImages: (productId, files) => {
      const formData = new FormData();
      files.forEach((file) => {
        formData.append("images", file);
      });
      return client
        .post(`/products/${productId}/images`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        })
        .then((r) => r.data);
    },
    deleteImage: (imageId) => http.del(`/products/images/${imageId}`),
  },
  messages: {
    getConversation: (userId, otherUserId, params) =>
      http.get(`/messages/${userId}/${otherUserId}`, { params }),
    getMessage: (id) => http.get(`/messages/single/${id}`),
    pin: (id) => http.post(`/messages/conversations/${id}/pin`),
    unpin: (id) => http.post(`/messages/conversations/${id}/unpin`),
    sendProductInquiry: (data) => http.post(`/messages/inquiry`, data),
  },
  files: {
    upload: (file) => {
      const form = new FormData();
      form.append("file", file);
      return client.post(`/files/upload`, form).then((r) => r.data);
    },
  },
  notifications: {
    list: (userId, params) => http.get(`/notifications/${userId}`, { params }),
    create: (data) => http.post(`/notifications`, data),
    markAsRead: (id) => http.patch(`/notifications/${id}/read`),
    markAllAsRead: () => http.patch(`/notifications/read-all`),
    getUnreadCount: (userId) => http.get(`/notifications/${userId}/unread-count`),
  },
  logger: {
    list: (params) => http.get("/logs", { params }),
    get: (id) => http.get(`/logs/${id}`),
    create: (payload) => http.post("/logs", payload),
    remove: (id) => http.del(`/logs/${id}`),
    getProfileVisitCount: (companyId) => http.get(`/logs/profile-visits/${companyId}`),
  },
  home: {
    hero: {
      get: () => http.get("/hero"),
    },
    brands: {
      list: () => http.get("/brands"),
    },
    challenges: {
      list: () => http.get("/challenges"),
    },
    features: {
      list: () => http.get("/features"),
    },
    testimonials: {
      list: () => http.get("/testimonials"),
    },
    faqs: {
      list: () => http.get("/faqs"),
    },
    footer: {
      get: () => http.get("/footer"),
    },
    common: {
      get: () => http.get("/common"),
    },
  },
  contacts: {
    create: (payload) => http.post("/contacts", payload),
    list: (params) => http.get("/contacts", { params }),
    get: (id) => http.get(`/contacts/${id}`),
  },
  requirements: {
    list: (params) => http.get("/requirements", { params }),
    get: (id) => http.get(`/requirements/${id}`),
    create: (payload) => http.post("/requirements/create", payload),
    update: (id, payload) => http.put(`/requirements/${id}`, payload),
    remove: (id) => http.del(`/requirements/${id}`),
    config: () => http.get("/requirements/config"),
  },
  tallyReports: {
    getCompanyDashboard: (params) => http.get("/dms/dashboard/company-dashboard", { params }),
    getManualCompanyPrimarySales: (params) => http.get("/dms/dashboard/manual-primary-sales", { params }),
    getManufacturerDashboard: (params) => http.get("/dms/dashboard/manufacturer", { params }),
    getManufacturerSummary: (params) => http.get("/dms/dashboard/manufacturer/summary", { params }),
    getManufacturerRankings: (params) =>
      http.get("/dms/dashboard/manufacturer/rankings", { params }),
    getAdminDashboard: (params) => http.get("/dms/dashboard/admin", { params }),
    getAdminSummary: (params) => http.get("/dms/dashboard/admin/summary", { params }),
    getAdminRankings: (params) => http.get("/dms/dashboard/admin/rankings", { params }),
    getFilterOptions: (params) => http.get("/dms/dashboard/filter-options", { params }),
    // New KPI API endpoints
    getKPIs: (params) => http.get("/dms/kpi", { params }),
    getManufacturerKPIs: (params) => http.get("/dms/kpi/manufacturer", { params }),
    getDistributorKPIs: (params) => http.get("/dms/kpi/distributor", { params }),
    getAdminKPIs: (params) => http.get("/dms/kpi/admin", { params }),
    // List APIs
    getManufacturers: (params) => http.get("/dms/manufacturers", { params }),
    getManufacturersFilterOptions: (params) =>
      http.get("/dms/manufacturers/filter-options", { params }),
    getDistributors: (params) => http.get("/dms/distributors", { params }),
    getDistributorsSales: (params) => http.get("/dms/distributors", { params }),
    getOutlets: (params) => http.get("/dms/outlets", { params }),
    getSKUs: (params) => http.get("/dms/skus", { params }),
    getSKUsDualContext: (params) => http.get("/dms/skus/dual-context", { params }),
    findSKUPage: (params) => http.get("/dms/skus/find-page", { params }),
    getStockItemsList: (params) => http.get("/dms/stock-items", { params }),
    updateStockItemIsSales: (id, payload, params) =>
      http.patch(`/dms/stock-items/${id}/is-sales`, payload, { params }),
    getInvoices: (params) => http.get("/dms/invoices", { params }),
    getInvoiceDetail: (id, params) => http.get(`/dms/invoices/${id}`, { params }),
    getSecondarySalesInvoices: (params) => http.get("/dms/invoices/secondary-sales", { params }),
    getSecondarySalesInvoiceDetail: (id, params) =>
      http.get(`/dms/invoices/secondary-sales/${id}`, { params }),
    getMappedPurchaseInvoices: (params) => http.get("/dms/invoices/mapped-purchases", { params }),
    getMappedPurchaseInvoiceDetail: (id, params) =>
      http.get(`/dms/invoices/mapped-purchases/${id}`, { params }),
    getStockItemNameByEntryId: (entryId) => http.get(`/dms/invoices/entry/${entryId}`),
    getSales: (params) => http.get("/dms/transactions/sales", { params }),
    getPurchase: (params) => http.get("/dms/transactions/purchase", { params }),
    getReceivable: (params) => http.get("/dms/transactions/receivable", { params }),
    getPayables: (params) => http.get("/dms/transactions/payables", { params }),
    refreshData: (payload) => http.post("/dms/exe/refresh_tally_data", payload),
    getLastSync: (companyId) => http.get(`/dms/tally-client/last-sync/${companyId}`),
    getLastSyncBatch: (companyId) => http.get(`/dms/tally-client/last-sync-batch/${companyId}`),
    getSyncStatusOverview: (params) =>
      http.get("/dms/tally-client/sync-status-overview", { params }),
    importSyncStatusBatch: (payload) => http.post("/dms/tally-client/sync-status-import", payload),
    updateSyncStatusPartnerCode: (payload) =>
      http.patch("/dms/tally-client/sync-status/partner-code", payload),
  },
  deals: {
    list: (params) => http.get("/deals/getmydeals", { params }),
    get: (id) => http.get(`/deals/${id}`),
    create: (payload) => http.post("/deals/create", payload),
    update: (id, payload) => http.put(`/deals/${id}`, payload),
    sign: (id, payload) => http.post(`/deals/${id}/sign`, payload),
    reject: (id, payload) => http.post(`/deals/${id}/reject`, payload),
    revoke: (id, payload) => http.post(`/deals/${id}/revoke`, payload),
    remove: (id) => http.del(`/deals/${id}`),
  },
  superAdmin: {
    login: (payload) => http.post("/superadmin/login", payload),
    register: (payload) => http.post("/superadmin/register", payload),
    getAdmins: (config) => http.get("/superadmin/users", withSuperAdminAuth(config)),
    createAdmin: (payload, config) =>
      http.post("/superadmin/users", payload, withSuperAdminAuth(config)),
    updateAdmin: (id, payload, config) =>
      http.put(`/superadmin/users/${id}`, payload, withSuperAdminAuth(config)),
    deleteAdmin: (id, config) => http.del(`/superadmin/users/${id}`, withSuperAdminAuth(config)),
  },
  manuDistriMapping: {
    list: (params) => http.get("/manu-distri-mapping", { params }),
    get: (id) => http.get(`/manu-distri-mapping/${id}`),
    create: (payload) => http.post("/manu-distri-mapping", payload),
    update: (id, payload) => http.put(`/manu-distri-mapping/${id}`, payload),
    remove: (id) => http.del(`/manu-distri-mapping/${id}`),
  },
  salesTeamMapping: {
    myDistributors: () => http.get("/sales-team-mapping/my-distributors"),
  },
  mappingStockItems: {
    list: (params, config) =>
      http.get("/mapping-stock-items", { params, ...withSuperAdminAuth(config) }),
    get: (id, config) => http.get(`/mapping-stock-items/${id}`, withSuperAdminAuth(config)),
    create: (payload, config) =>
      http.post("/mapping-stock-items", payload, withSuperAdminAuth(config)),
    update: (id, payload, config) =>
      http.put(`/mapping-stock-items/${id}`, payload, withSuperAdminAuth(config)),
    remove: (id, config) => http.del(`/mapping-stock-items/${id}`, withSuperAdminAuth(config)),
  },
  mappingRequests: {
    list: (params) => http.get("/mapping-requests/list-requests", { params }),
    get: (id, params) => http.get(`/mapping-requests/get-request/${id}`, { params }),
    create: (payload) => http.post("/mapping-requests/create-request", payload),
    accept: (id) => http.post(`/mapping-requests/${id}/accept`),
    reject: (id) => http.post(`/mapping-requests/${id}/reject`),
    updateSkuName: (requestId, skuId, payload) =>
      http.patch(`/mapping-requests/${requestId}/skus/${skuId}`, payload),
    replaceSku: (requestId, skuId, payload) =>
      http.put(`/mapping-requests/${requestId}/skus/${skuId}`, payload),
    deleteSku: (requestId, skuId) => http.del(`/mapping-requests/${requestId}/skus/${skuId}`),
    addSkus: (requestId, payload) => http.post(`/mapping-requests/${requestId}/skus`, payload),
    productAnalytics: (payload) => http.post("/mapping-requests/product-analytics", payload),
  },
  // Use normal auth (company user token) for mapping stock items when on admin secondary-sales flow
  mappingStockItemsCompany: {
    list: (params) => http.get("/mapping-stock-items", { params }),
    create: (payload) => http.post("/mapping-stock-items", payload),
    update: (id, payload) => http.put(`/mapping-stock-items/${id}`, payload),
  },
  labels: {
    list: (params) => http.get("/labels/getAlllabels", { params }),
    get: (id) => http.get(`/labels/get/${id}`),
    getActive: (params) => http.get("/labels/active", { params }),
    create: (payload) => http.post("/labels/create", payload),
    update: (id, payload) => http.put(`/labels/update/${id}`, payload),
    inactivate: (id) => http.patch(`/labels/${id}/inactive`),
    activate: (id) => http.patch(`/labels/${id}/active`),
    getStockItemsByLabelId: (id) => http.get(`/labels/${id}/stock-items`),
    getLabelsByStockItemId: (stockItemId) => http.get(`/labels/by-stock-item/${stockItemId}`),
    assign: (payload) => http.post("/labels/assign", payload),
    unassign: (payload) => http.del("/labels/unassign", { data: payload }),
    searchActive: (search) => http.get("/labels/getAlllabels", { params: { search, is_active: true, pageSize: 100 } }),
  },
  userLabels: {
    getUsersByLabel: (labelId) => http.get(`/user-labels/${labelId}/users`),
    getMemberCount: (labelId) => http.get(`/user-labels/${labelId}/member-count`),
    assignUsers: (labelId, payload) => http.post(`/user-labels/${labelId}/assign`, payload),
    unassignUser: (labelId, userId) => http.del(`/user-labels/${labelId}/users/${userId}`),
    getLabelsByUser: (userId) => http.get(`/user-labels/by-user/${userId}`),
  },
  companyRoles: {
    listActive: (params) => http.get("/company-roles/active", { params }),
  },
  labelLedgers: {
    getLabelsByLedgerId: (ledgerId) => http.get(`/label-ledgers/by-ledger/${ledgerId}`),
    assign: (payload) => http.post("/label-ledgers/assign", payload),
    unassign: (payload) => http.del("/label-ledgers/unassign", { data: payload }),
  },
  industryCategories: {
    list: (params) => http.get("/bizz-industry-categories/getAll", { params }),
    get: (id) => http.get(`/bizz-industry-categories/get/${id}`),
    getActive: (params) => http.get("/bizz-industry-categories/active", { params }),
    create: (payload) => http.post("/bizz-industry-categories/create", payload),
    update: (id, payload) => http.put(`/bizz-industry-categories/update/${id}`, payload),
    remove: (id) => http.del(`/bizz-industry-categories/delete/${id}`),
    activate: (id) => http.patch(`/bizz-industry-categories/${id}/activate`),
    deactivate: (id) => http.patch(`/bizz-industry-categories/${id}/deactivate`),
  },
  industrySubcategories: {
    list: (params) => http.get("/bizz-industry-subcategories/getAll", { params }),
    get: (id) => http.get(`/bizz-industry-subcategories/get/${id}`),
    getActive: (params) => http.get("/bizz-industry-subcategories/active", { params }),
    getByCategory: (categoryId, params) => http.get(`/bizz-industry-subcategories/by-category/${categoryId}`, { params }),
    getAllGrouped: (params) => http.get("/bizz-industry-subcategories/all-grouped", { params }),
    create: (payload) => http.post("/bizz-industry-subcategories/create", payload),
    update: (id, payload) => http.put(`/bizz-industry-subcategories/update/${id}`, payload),
    remove: (id) => http.del(`/bizz-industry-subcategories/delete/${id}`),
    activate: (id) => http.patch(`/bizz-industry-subcategories/${id}/activate`),
    deactivate: (id) => http.patch(`/bizz-industry-subcategories/${id}/deactivate`),
  },
  stockItemIndustry: {
    list: (params) => http.get("/stock-item-bizz-industry", { params }),
    get: (id) => http.get(`/stock-item-bizz-industry/${id}`),
    getByStockItemId: (stockItemId) => http.get(`/stock-item-bizz-industry/by-stock-item/${stockItemId}`),
    getSkusWithAssignment: (params) => http.get("/stock-item-bizz-industry/skus-with-assignment", { params }),
    getUnmappedSkus: (params) => http.get("/stock-item-bizz-industry/unmapped-skus", { params }),
    create: (payload) => http.post("/stock-item-bizz-industry", payload),
    update: (id, payload) => http.put(`/stock-item-bizz-industry/${id}`, payload),
    remove: (id, companyId) =>
      http.del(`/stock-item-bizz-industry/${id}`, companyId ? { params: { company_id: companyId } } : undefined),
    unassignSpecific: (stockItemId, payload) =>
      http.post(`/stock-item-bizz-industry/unassign-specific/${stockItemId}`, payload),
    assignMultiple: (payload) => http.post("/stock-item-bizz-industry/assign-multiple-subcategories", payload),
  },
  stockItems: {
    getAssignedDistributors: (stockItemId) => http.get(`/dms/stock-items/${stockItemId}/assigned-distributors`),
  },
  cashBank: {
    getTotal: (params) => http.get("/dms/cash-bank/total", { params }),
    getBanks: (params) => http.get("/dms/cash-bank/banks", { params }),
    getCash: (params) => http.get("/dms/cash-bank/cash", { params }),
  },
  // Manufacturer only — installation leads read from EmpMgt (MongoDB)
  installations: {
    me: () => http.get("/installations/me"),
    summary: (params) => http.get("/installations/summary", { params }),
    performance: (params) => http.get("/installations/performance", { params }),
    activity: (params) => http.get("/installations/activity", { params }),
    leads: (params) => http.get("/installations/leads", { params }),
    lead: (id) => http.get(`/installations/leads/${id}`),
    leadComments: (id, params) => http.get(`/installations/leads/${id}/comments`, { params }),
    addLeadComment: (id, payload) => http.post(`/installations/leads/${id}/comments`, payload),
  },
  // Support tickets — raised by any logged-in user, managed by the team in EmpMgt
  support: {
    summary: () => http.get("/support/summary"),
    tickets: (params) => http.get("/support/tickets", { params }),
    ticket: (id) => http.get(`/support/tickets/${id}`),
    create: (formData, onUploadProgress) =>
      http.post("/support/tickets", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 10 * 60 * 1000,
        onUploadProgress,
      }),
    addComment: (id, payload) => http.post(`/support/tickets/${id}/comments`, payload),
  },

};

export default Api;